import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import riverFragmentsHtml from '../../../../../../新規追加ミニゲーム/river_fragments.html?raw';
import voiceRestoreHtml from '../../../../../../新規追加ミニゲーム/voice_restore.html?raw';
import somaDistortionHtml from '../../../../../../新規追加ミニゲーム/mg_soma_distortion.html?raw';
import { audioEngine } from '../../../audio/audioEngine';
import { useManualSubmit } from '../hooks';

const VARIANT_META = {
  river_fragments: {
    source: 'river_fragments.html',
    title: '川の主の断片',
    html: riverFragmentsHtml
  },
  soma_distortion: {
    source: 'mg_soma_distortion.html',
    title: 'そまの傷ノイズ観測',
    html: somaDistortionHtml
  },
  voice_restore: {
    source: 'voice_restore.html',
    title: '火を声へ戻す',
    html: voiceRestoreHtml
  }
};

const DEFAULT_CONFIG = {
  templateId: 20,
  variant: 'river_fragments',
  title: '杣川の呼び火',
  submitButtonLabel: '結果を送る',
  fragments: ['いろ', 'こえ', 'へる', 'いたい', 'のこる'],
  observedClues: ['clue_repainted_color', 'clue_reduced_voices', 'clue_pain_remains'],
  restoredFragments: ['そま行こ', 'そまで遊ぼ'],
  revealedNameHint: 'そま'
};

const Template20SomaCallingFire = ({
  hostSubmit,
  hostConfig,
  registryId = 'minigame_soma_calling_fire_20',
  templateId = 20
}) => {
  const iframeRef = useRef(null);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const resolved = useMemo(() => resolveConfig(hostConfig), [hostConfig]);
  const srcDoc = useMemo(() => buildSomaSrcDoc(resolved), [resolved]);

  useEffect(() => {
    resetSubmission();
  }, [resetSubmission, resolved.variant, resolved.title]);

  // P8-1: グローバルミュート/音量を srcdoc iframe（独自 AudioContext・destination 直結）へ同期する。
  // sandbox="allow-scripts"（allow-same-origin 無し）のため直接触れない → HOST_SET_SOUND 形の
  // postMessage で通知し、iframe 側の受信スクリプト（buildAudioSyncScript）が suspend/resume する。
  // targetOrigin は sandbox iframe の origin が opaque（'null'）なので '*'（機微情報は含まない）。
  const postAudioState = useCallback(() => {
    const frame = iframeRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage(
      {
        v: 1,
        type: 'HOST_SET_SOUND',
        runId: 'react-t20',
        stepId: '',
        templateId: 20,
        ts: Date.now(),
        payload: { muted: audioEngine.isMuted(), volume: audioEngine.getMasterVolume() }
      },
      '*'
    );
  }, []);

  useEffect(() => {
    // マウント中のミュート/音量変更に追従（初期値は iframe onLoad 時に送る）。
    const unsubscribe = audioEngine.onChange(() => postAudioState());
    return unsubscribe;
  }, [postAudioState]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (iframeRef.current?.contentWindow && event.source !== iframeRef.current.contentWindow) {
        return;
      }
      const data = event.data;
      if (!data || data.type !== 'soma-calling-fire-result') {
        return;
      }

      submit(data.status === 'failure' || data.status === 'fail' ? 'failure' : 'success', {
        score: Number.isFinite(data.score) ? data.score : 100,
        variant: resolved.variant,
        title: resolved.title,
        fragments: data.fragments ?? resolved.fragments,
        observedClues: data.observedClues ?? resolved.observedClues,
        restoredFragments: data.restoredFragments ?? resolved.restoredFragments,
        revealedNameHint: data.revealedNameHint ?? resolved.revealedNameHint,
        source: data.source ?? VARIANT_META[resolved.variant].source
      });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [resolved, submit]);

  return (
    <div style={styles.shell}>
      <iframe
        key={`${resolved.variant}:${resolved.title}`}
        ref={iframeRef}
        title={resolved.title}
        srcDoc={srcDoc}
        sandbox="allow-scripts"
        style={styles.frame}
        onLoad={postAudioState}
      />
    </div>
  );
};

function resolveConfig(config) {
  const variant = config?.variant && VARIANT_META[config.variant] ? config.variant : DEFAULT_CONFIG.variant;
  return {
    ...DEFAULT_CONFIG,
    ...(config ?? {}),
    templateId: 20,
    variant,
    title: config?.title ?? VARIANT_META[variant].title,
    fragments: Array.isArray(config?.fragments) && config.fragments.length > 0
      ? config.fragments
      : DEFAULT_CONFIG.fragments,
    observedClues: Array.isArray(config?.observedClues) && config.observedClues.length > 0
      ? config.observedClues
      : DEFAULT_CONFIG.observedClues,
    restoredFragments: Array.isArray(config?.restoredFragments) && config.restoredFragments.length > 0
      ? config.restoredFragments
      : DEFAULT_CONFIG.restoredFragments,
    revealedNameHint: config?.revealedNameHint ?? DEFAULT_CONFIG.revealedNameHint,
    submitButtonLabel: config?.submitButtonLabel ?? DEFAULT_CONFIG.submitButtonLabel
  };
}

function buildSomaSrcDoc(config) {
  const meta = VARIANT_META[config.variant] ?? VARIANT_META.river_fragments;
  const bridgeConfig = JSON.stringify({
    variant: config.variant,
    title: config.title,
    submitButtonLabel: config.submitButtonLabel,
    fragments: config.fragments,
    observedClues: config.observedClues,
    restoredFragments: config.restoredFragments,
    revealedNameHint: config.revealedNameHint,
    source: meta.source
  }).replace(/</g, '\\u003c');

  const headPatch = `
<style>
  html, body { width: 100%; height: 100%; background: #050608; }
  body { overflow: hidden; }
  .soma-submit-button {
    margin-top: 18px;
    appearance: none;
    border: 1px solid rgba(216,210,191,0.7);
    border-radius: 999px;
    background: rgba(5, 8, 12, 0.78);
    color: #f4ecd8;
    padding: 11px 22px;
    font: inherit;
    letter-spacing: 0.14em;
    cursor: pointer;
    box-shadow: 0 0 24px rgba(168,225,255,0.18);
  }
  .soma-submit-button:disabled {
    opacity: 0.55;
    cursor: default;
  }
  .soma-submit-button--fixed {
    position: fixed;
    left: 50%;
    bottom: max(28px, env(safe-area-inset-bottom, 0px) + 18px);
    transform: translateX(-50%);
    z-index: 80;
  }
  .soma-embedded.soma-variant-river_fragments .overlay .lead {
    max-width: 300px;
    margin-left: auto;
    margin-right: auto;
    font-size: clamp(14px, 3.6vw, 16px);
    line-height: 1.8;
  }
  .soma-embedded.soma-variant-river_fragments .overlay .note {
    opacity: 0.58;
  }
  .soma-embedded.soma-variant-river_fragments .completion .next {
    display: none;
  }
</style>`;

  return meta.html
    .replace('</head>', `${headPatch}\n</head>`)
    .replace('</body>', `${buildVisualPatchScript(bridgeConfig)}\n${buildBridgeScript(config.variant, bridgeConfig)}\n${buildAudioSyncScript()}\n</body>`);
}

/**
 * P8-1: グローバルミュート/音量の受信スクリプト（全 variant 共通で srcdoc 末尾に注入）。
 *
 * 対象 HTML（river_fragments / voice_restore）は独自 AudioContext を destination 直結で
 * 使っており、Player のグローバルミュートに連動しなかった。親から届く HOST_SET_SOUND
 * （{muted, volume}）を受けて:
 *  - muted: AudioContext を suspend / 解除で resume（自分が suspend した時だけ resume する）
 *  - volume: masterGain がある variant（river_fragments）のみ初期値×volume で反映
 *    （voice_restore は各音が destination 直結のため音量は非対応＝mute のみ連動）
 *
 * audioCtx はユーザー操作で遅延生成されるため、受信時 apply に加えて軽量ポーリングで
 * 生成後にも desired 状態を反映する。mg_soma_distortion は AudioContext を持たないため
 * 全分岐が no-op（安全）。
 */
function buildAudioSyncScript() {
  return `
<script>
(function () {
  var desired = null;
  var suspendedByHost = false;
  var baseMasterGain = null;

  function apply() {
    if (!desired) return;
    try {
      var ctx = (typeof audioCtx !== 'undefined') ? audioCtx : null;
      if (ctx) {
        if (desired.muted && ctx.state === 'running') {
          suspendedByHost = true;
          ctx.suspend();
        } else if (!desired.muted && suspendedByHost && ctx.state === 'suspended') {
          suspendedByHost = false;
          ctx.resume();
        }
      }
      if (typeof masterGain !== 'undefined' && masterGain && masterGain.gain && typeof desired.volume === 'number') {
        if (baseMasterGain == null) baseMasterGain = masterGain.gain.value || 0.7;
        masterGain.gain.value = baseMasterGain * Math.max(0, Math.min(1, desired.volume));
      }
    } catch (e) { /* 音同期の失敗は無視（鳴り方が変わらないだけ） */ }
  }

  window.addEventListener('message', function (ev) {
    var d = ev.data;
    if (!d || d.type !== 'HOST_SET_SOUND' || !d.payload) return;
    desired = {
      muted: !!d.payload.muted,
      volume: typeof d.payload.volume === 'number' ? d.payload.volume : null
    };
    apply();
  });

  setInterval(apply, 400);
}());
</script>`;
}

function buildVisualPatchScript(bridgeConfig) {
  return `
<script>
(function () {
  var config = ${bridgeConfig};
  function applyVisualPatch() {
    document.documentElement.classList.add('soma-embedded', 'soma-variant-' + config.variant);
    document.title = config.title || document.title;

    if (config.variant === 'river_fragments') {
      var title = document.querySelector('.overlay .title');
      var subtitle = document.querySelector('.overlay .subtitle');
      var lead = document.querySelector('.overlay .lead');
      var start = document.getElementById('startBtn');
      var next = document.querySelector('.completion .next');
      if (title) title.textContent = '川を見る';
      if (subtitle) subtitle.textContent = '水面に浮かぶ声を拾う';
      if (lead) lead.innerHTML = '水面に言葉が浮かんでいる。<br>まだ意味を決めず、端末ログへ残す。';
      if (start) start.textContent = '水面を見る';
      if (next) next.textContent = '';
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyVisualPatch);
  } else {
    applyVisualPatch();
  }
}());
</script>`;
}

function buildBridgeScript(variant, bridgeConfig) {
  switch (variant) {
    case 'soma_distortion':
      return buildDistortionBridge(bridgeConfig);
    case 'voice_restore':
      return buildVoiceRestoreBridge(bridgeConfig);
    case 'river_fragments':
    default:
      return buildRiverFragmentsBridge(bridgeConfig);
  }
}

function buildRiverFragmentsBridge(bridgeConfig) {
  return `
<script>
(function () {
  var config = ${bridgeConfig};
  var sent = false;

  function sendResult() {
    if (sent) return;
    sent = true;
    parent.postMessage({
      type: 'soma-calling-fire-result',
      status: 'success',
      score: 100,
      fragments: config.fragments,
      source: config.source
    }, '*');
  }

  function addSubmitButton() {
    var completion = document.getElementById('completion');
    if (!completion || completion.querySelector('[data-soma-submit]')) return;
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'soma-submit-button';
    button.setAttribute('data-soma-submit', 'true');
    button.textContent = config.submitButtonLabel || '結果を送る';
    button.addEventListener('click', sendResult);
    completion.appendChild(button);
  }

  var originalShowCompletion = window.showCompletion;
  if (typeof originalShowCompletion === 'function') {
    window.showCompletion = function () {
      originalShowCompletion.apply(this, arguments);
      setTimeout(addSubmitButton, 80);
    };
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (document.getElementById('completion')?.classList.contains('visible')) {
      addSubmitButton();
    }
  });
}());
</script>`;
}

function buildDistortionBridge(bridgeConfig) {
  return `
<script>
(function () {
  var config = ${bridgeConfig};
  var sent = false;
  var completionDetail = null;

  function sendResult() {
    if (sent) return;
    sent = true;
    var clues = completionDetail && Array.isArray(completionDetail.clues)
      ? completionDetail.clues
      : config.observedClues;
    parent.postMessage({
      type: 'soma-calling-fire-result',
      status: 'success',
      score: 100,
      observedClues: clues,
      somaDistortionSeen: true,
      source: config.source
    }, '*');
  }

  document.addEventListener('mg_soma_distortion:complete', function (event) {
    completionDetail = event.detail || null;
    var button = document.getElementById('reveal-next');
    if (button) {
      button.textContent = config.submitButtonLabel || '結果を送る';
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    var button = document.getElementById('reveal-next');
    if (!button) return;
    button.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      sendResult();
    }, true);
  });
}());
</script>`;
}

function buildVoiceRestoreBridge(bridgeConfig) {
  return `
<script>
(function () {
  var config = ${bridgeConfig};
  var sent = false;

  function sendResult() {
    if (sent) return;
    sent = true;
    parent.postMessage({
      type: 'soma-calling-fire-result',
      status: 'success',
      score: 100,
      restoredFragments: config.restoredFragments,
      revealedNameHint: config.revealedNameHint,
      source: config.source
    }, '*');
  }

  function addSubmitButton() {
    var overlay = document.getElementById('finaleOverlay') || document.getElementById('app') || document.body;
    if (!overlay || overlay.querySelector('[data-soma-submit]')) return;
    var retry = document.getElementById('retryBtn');
    var button = document.createElement('button');
    button.type = 'button';
    button.className = retry ? retry.className : 'soma-submit-button soma-submit-button--fixed';
    button.classList.add('soma-submit-button');
    button.setAttribute('data-soma-submit', 'true');
    button.textContent = config.submitButtonLabel || '結果を送る';
    button.addEventListener('click', sendResult);
    if (retry && retry.parentNode) {
      retry.parentNode.insertBefore(button, retry);
      setTimeout(function () { button.classList.add('show'); }, 80);
    } else {
      overlay.appendChild(button);
    }
  }

  var originalShowFinale = window.showFinale;
  if (typeof originalShowFinale === 'function') {
    window.showFinale = function () {
      originalShowFinale.apply(this, arguments);
      setTimeout(addSubmitButton, 3600);
    };
  }
}());
</script>`;
}

const styles = {
  shell: {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    background: 'radial-gradient(ellipse at 50% 20%, #18252c 0%, #081012 64%, #050608 100%)'
  },
  frame: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    border: 0,
    display: 'block',
    background: 'transparent'
  }
};

export default Template20SomaCallingFire;
